import { produce } from "immer";
import { dateRangeOperators } from "../../../Pages/DataManagement/DataExplorer/DataExplorerUI/ExplorerInputs/FieldOperatorList";
import { convertFiltersToConfigFormat } from "../../../Editors/BlockEdit/VisualizationGuiEditSection/convertFiltersToOperatorsMode";
import { omit } from "lodash-es";

export default function prepareSettings(rawSettings, saving) {
  const settings = conformOrders(rawSettings);
  const strippedFilters = filtersWithValues(settings);
  const strippedHavings = havingsWithDefinedOperators(settings);

  let nextFilters = convertSpecialTypes(strippedFilters);
  nextFilters = convertToWithNull(nextFilters);

  // do not build groupType OR on save settings
  if (!saving) {
    nextFilters = buildGroupByOrFilters(nextFilters);
  }

  let ret = { ...settings, filters: nextFilters, havings: strippedHavings };
  if (!ret.filters.length) {
    delete ret.filters;
  }
  if (!ret.havings.length) {
    delete ret.havings;
  }
  return ret;
}

function conformOrders(settings) {
  if (!settings?.orders) return settings;
  const order = settings.orders.reduce((acc, curr) => {
    return { ...acc, [curr.name]: curr.sort };
  }, {});
  const draftSettings = { ...settings, order };
  delete draftSettings.orders;
  return draftSettings;
}

function filtersWithValues(settings) {
  return (settings?.filters ?? []).filter(
    (fil) => fil.subQueryValues || fil.values?.length || fil.filters?.length
  );
}

function havingsWithDefinedOperators(settings) {
  return (settings?.havings ?? [])
    .filter((having) => having.operator)
    .filter((having) => having.values?.length);
}

function convertSpecialTypes(filters) {
  return produce(filters, (draftState) => {
    draftState.forEach((filter) => {
      if (dateRangeOperators.includes(filter.operator)) {
        delete filter.operator;
      }
    });
  });
}

export function convertToWithNull(filters) {
  return produce(filters, (draftFilters) => {
    for (const filter of draftFilters) {
      if (2 <= filter.values?.length && filter.values.includes(null)) {
        filter.values = filter.values.filter((v) => v !== null);
        filter.withNull = true;
      }
    }
  });
}

export function configureSubQueryIfExist(filter) {
  const { subQueryValues, type, value, operator, ignoreCaseSensitivity } =
    filter;

  if (subQueryValues) {
    const { type: name, aggregation, value, operator } = subQueryValues;

    return {
      name: type,
      subQueryValues: {
        name,
        values: [value],
        operator,
        aggregation: { type: aggregation },
      },
      ...(ignoreCaseSensitivity && { ignoreCaseSensitivity }),
    };
  }

  return {
    name: type,
    values: [value],
    ...(operator && { operator }),
    ...(ignoreCaseSensitivity && { ignoreCaseSensitivity }),
  };
}

// combine values for filters with same name (groupType OR/AND)
export function mergeGroupFilterValuesByName(grouped) {
  return {
    ...grouped,
    filters: grouped.filters.map(mergeGroupFilters),
  };
}

function mergeGroupFilters(group) {
  return {
    ...group,
    filters: group.filters.reduce(mergeByName, []),
  };
}

function isEqualityOperatorsOnly(a, b) {
  const noOpearot = !a.operator && !b.operator;
  const equalOperator = a.operator === "==" && b.operator === "==";

  return noOpearot || equalOperator;
}

function mergeByName(acc, curr) {
  const index = acc.findIndex((item) => item.name === curr.name);

  // this should group only for operator "=="
  if (index > -1 && isEqualityOperatorsOnly(acc[index], curr)) {
    acc[index].values = mergeUniqueValues(acc[index].values, curr.values);
  } else {
    acc.push({ ...curr });
  }

  return acc;
}

function mergeUniqueValues(arr1, arr2) {
  return Array.from(new Set([...arr1, ...arr2]));
}

export function getConfiguredGroupTypeItem(
  groupedFilters,
  savedModeFilters,
  applyConversion
) {
  return {
    groupType: "OR",
    ...(savedModeFilters && { savedModeFilters }),
    filters: groupedFilters.map((group) => {
      const convertedFilters = applyConversion
        ? convertFiltersToConfigFormat(group.filters, [])
        : group;

      return {
        groupType: "AND",
        filters: convertedFilters.map(configureSubQueryIfExist),
      };
    }),
  };
}

// oposite of getConfiguredGroupTypeItem
// for scheduled emails we need to save filters in attachments in correct exec format
// with groupType "OR"/"AND" but for filter explorer we need to convert this back
export function getBackConvertedGroupTypeItem(groupedFilters) {
  return (groupedFilters ?? []).reduce((acc, curr) => {
    if (!curr.filters) {
      acc.push(curr);
    } else {
      curr.filters.forEach((filter) => {
        acc.push({
          filters: (filter.filters ?? []).map((filter) => ({
            ...filter,
            operator: filter.operator ?? "==",
          })),
        });
      });
    }

    return acc;
  }, []);
}

// this is analog of OR / AND structure in query builder
export function buildGroupByOrFilters(filters) {
  const cleanedFilters = filters.map((filter) =>
    filter.filters?.length ? filter : omit(filter, "filters")
  );

  const groupedFilters = filters.filter((filter) => filter.filters?.length);
  const standaloneFilters = filters.filter((filter) => !filter.filters?.length);

  if (!groupedFilters.length) return standaloneFilters;

  const orGroupBlock = mergeGroupFilterValuesByName(
    getConfiguredGroupTypeItem(groupedFilters, cleanedFilters, true)
  );

  return [orGroupBlock, ...standaloneFilters];
}

// do not save filters with groupType OR, save in data explorer mode
export function getSavedModeFilters(filters) {
  return (filters ?? []).reduce((acc, curr) => {
    if (curr.savedModeFilters) {
      acc.push(...curr.savedModeFilters);
    } else {
      acc.push(curr);
    }

    return acc;
  }, []);
}
